import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Polyline } from "react-leaflet";
import { fetchRoute } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { OSM_TILE_URL, OSM_ATTRIBUTION, tileClassForTheme } from "@/lib/tiles";
import { ArrowRight, MapPin } from "lucide-react";

const SLUGS = ["waw-cork", "ring-of-kerry", "waw-clare", "waw-connemara-loop", "waw-sligo-mayo", "waw-donegal"];
const ITINERARY = [
    { days: "Day 1–2", seg: "waw-cork", label: "Cork & Kinsale", km: 500, sleep: "Kinsale → Bantry" },
    { days: "Day 3–4", seg: "ring-of-kerry", label: "Kerry Peninsulas", km: 432, sleep: "Kenmare → Dingle" },
    { days: "Day 5", seg: "waw-clare", label: "Clare & Cliffs of Moher", km: 214, sleep: "Doolin" },
    { days: "Day 6", seg: "waw-connemara-loop", label: "Galway & Connemara", km: 180, sleep: "Clifden" },
    { days: "Day 7–8", seg: "waw-sligo-mayo", label: "Mayo & Sligo", km: 477, sleep: "Westport → Strandhill" },
    { days: "Day 9–10", seg: "waw-donegal", label: "Donegal & Inishowen", km: 515, sleep: "Ardara → Malin" },
];

export default function RideTheWholeThing() {
    const [polylines, setPolylines] = useState({});
    const { theme } = useTheme();

    useEffect(() => {
        document.title = "Ride The Whole Thing · The Full Wild Atlantic Way";
        Promise.all(SLUGS.map(fetchRoute)).then((rs) => {
            const m = {}; rs.forEach((r) => { m[r.slug] = r.polyline; }); setPolylines(m);
        }).catch(() => {});
    }, []);

    const total = ITINERARY.reduce((s, d) => s + d.km, 0);

    return (
        <div data-testid="ride-whole-thing-page">
            <section className="pt-40 pb-16 border-b border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow fade-up">The Whole Ribbon</div>
                    <h1 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter fade-up fade-up-delay-1">
                        Ride the whole<br /><span className="text-[#E87722]">2,500 km</span>.
                    </h1>
                    <p className="mt-8 font-body text-[var(--fg-muted)] text-lg max-w-2xl fade-up fade-up-delay-2">
                        Every WAW segment on one map, broken into a 10-day suggested itinerary. Download all 6 GPX files and go.
                    </p>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 border border-[var(--border-hair)] overflow-hidden" style={{ height: "640px" }}>
                        <MapContainer center={[53.5, -8.5]} zoom={7} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }} className={`z-0 ${tileClassForTheme(theme)}`}>
                            <TileLayer
                                url={OSM_TILE_URL}
                                attribution={OSM_ATTRIBUTION}
                            />
                            {Object.entries(polylines).map(([s, p]) => (
                                <Polyline key={s} positions={p} pathOptions={{ color: "#E87722", weight: 3.5, opacity: 0.9 }} />
                            ))}
                        </MapContainer>
                    </div>
                    <div className="border border-[var(--border-hair)] bg-[var(--card-bg)] p-6">
                        <div className="eyebrow mb-4">10-Day Itinerary</div>
                        <div className="font-display font-black text-4xl text-[var(--fg)] leading-none">{total.toLocaleString()} <span className="text-lg text-[var(--fg-dim)]">km</span></div>
                        <div className="text-xs uppercase tracking-[0.24em] text-[var(--fg-dim)] mt-1 mb-6">Kinsale → Malin Head</div>
                        <div className="divide-y divide-[var(--border-hair)]">
                            {ITINERARY.map((d, i) => (
                                <Link key={i} to={`/routes/${d.seg}`} data-testid={`itinerary-${d.seg}`} className="group block py-4">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-[10px] uppercase tracking-[0.24em] text-[#E87722]">{d.days}</div>
                                            <div className="font-display font-bold uppercase text-sm text-[var(--fg)] group-hover:text-[#E87722] truncate">{d.label}</div>
                                            <div className="text-xs text-[var(--fg-dim)] mt-1 flex items-center gap-1"><MapPin size={10} />{d.sleep}</div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="font-display font-black text-lg text-[var(--fg)]">{d.km}</div>
                                            <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--fg-dim)]">km</div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-24 border-t border-[var(--border-hair)] text-center">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-display font-black uppercase text-[var(--fg)] text-4xl md:text-6xl leading-none tracking-tight">Start where you like.</h2>
                    <p className="mt-6 font-body text-[var(--fg-muted)]">Every segment links to its own route page with real GPX, cafés and safety notes.</p>
                    <Link to="/routes" data-testid="rtwt-cta" className="mt-10 inline-flex items-center gap-3 bg-[#E87722] hover:bg-[#F28C38] text-black px-10 py-4 font-display font-bold uppercase tracking-[0.2em]">All Routes <ArrowRight size={18} /></Link>
                </div>
            </section>
        </div>
    );
}
